import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { IUserKeywordGroup } from "../keywordGroups.types";
import { deleteKeywordGroup, keywordGroupsKeys } from "../queries/keywordGroupsQueries";

interface IDeleteKeywordGroupParams {
    group: IUserKeywordGroup;
}

/**
 * @deprecated Use useDeleteKeywordGroupApiClient from pro-api-client instead.
 * This hook will be removed in a future version.
 * New implementations should use the API client version for better type safety and consistency.
 */
export const useDeleteKeywordGroup = (
    options?: Omit<UseMutationOptions<IUserKeywordGroup[], Error, IDeleteKeywordGroupParams>, "mutationFn">,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ group }: IDeleteKeywordGroupParams) => {
            return await deleteKeywordGroup(group.Id, group.Name, group.Keywords);
        },
        onSuccess: async (result, { group }, context) => {
            queryClient.setQueryData(keywordGroupsKeys.user(), (old: IUserKeywordGroup[] = []) =>
                old.filter((g) => g.Id !== group.Id),
            );

            queryClient.removeQueries({ queryKey: keywordGroupsKeys.byId(group.Id) });
            await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });

            options?.onSuccess?.(result, { group }, context);
        },
        onError: (error, variables, context) => {
            options?.onError?.(error, variables, context);
        },
        ...options,
    });
};
